import { useState } from 'react'
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  ToggleButtonGroup,
  ToggleButton,
  Typography,
  Chip,
} from '@mui/material'
import { createRRule, getRRuleDescription } from '@/lib/recurrence'

interface RecurrenceBuilderProps {
  value: string | null
  onChange: (rrule: string | null) => void
}

type FrequencyType = 'NONE' | 'DAILY' | 'WEEKLY' | 'MONTHLY' | 'YEARLY'

export default function RecurrenceBuilder({
  value,
  onChange,
}: RecurrenceBuilderProps) {
  const [frequency, setFrequency] = useState<FrequencyType>('NONE')
  const [interval, setInterval] = useState(1)
  const [selectedDays, setSelectedDays] = useState<number[]>([])
  const [monthDay, setMonthDay] = useState(1)
  const [monthPosition, setMonthPosition] = useState<number | null>(null)

  const handleFrequencyChange = (newFreq: FrequencyType) => {
    setFrequency(newFreq)

    if (newFreq === 'NONE') {
      onChange(null)
      return
    }

    // Generate RRule based on frequency
    const params: any = { frequency: newFreq, interval }

    if (newFreq === 'WEEKLY' && selectedDays.length > 0) {
      params.byweekday = selectedDays
    }

    if (newFreq === 'MONTHLY') {
      if (monthPosition !== null && selectedDays.length > 0) {
        params.byweekday = selectedDays
        params.bysetpos = monthPosition
      } else {
        params.bymonthday = monthDay
      }
    }

    const rrule = createRRule(params)
    onChange(rrule)
  }

  const handleIntervalChange = (newInterval: number) => {
    setInterval(newInterval)
    if (frequency !== 'NONE') {
      handleFrequencyChange(frequency)
    }
  }


  const dayLabels = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      {/* Frequency Selection */}
      <FormControl fullWidth>
        <InputLabel>Recurrence</InputLabel>
        <Select
          value={frequency}
          label="Recurrence"
          onChange={(e) => handleFrequencyChange(e.target.value as FrequencyType)}
        >
          <MenuItem value="NONE">One-time (No Recurrence)</MenuItem>
          <MenuItem value="DAILY">Daily</MenuItem>
          <MenuItem value="WEEKLY">Weekly</MenuItem>
          <MenuItem value="MONTHLY">Monthly</MenuItem>
          <MenuItem value="YEARLY">Yearly</MenuItem>
        </Select>
      </FormControl>

      {/* Interval */}
      {frequency !== 'NONE' && (
        <TextField
          label={`Repeat every (${frequency.toLowerCase()})`}
          type="number"
          value={interval}
          onChange={(e) => handleIntervalChange(parseInt(e.target.value) || 1)}
          inputProps={{ min: 1, max: 99 }}
          helperText={
            interval > 1
              ? `Every ${interval} ${frequency.toLowerCase().slice(0, -2)}s`
              : ''
          }
        />
      )}

      {/* Weekly: Day Selection */}
      {frequency === 'WEEKLY' && (
        <Box>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            On these days
          </Typography>
          <ToggleButtonGroup
            value={selectedDays}
            onChange={(_, newDays) => {
              setSelectedDays(newDays)
              handleFrequencyChange(frequency)
            }}
            aria-label="days of week"
            sx={{ flexWrap: 'wrap' }}
          >
            {dayLabels.map((day, index) => (
              <ToggleButton
                key={day}
                value={index}
                aria-label={day}
                sx={{ minWidth: 45 }}
              >
                {day}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>
      )}

      {/* Monthly: Advanced Options */}
      {frequency === 'MONTHLY' && (
        <Box>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Monthly Pattern</InputLabel>
            <Select
              value={monthPosition !== null ? 'position' : 'day'}
              label="Monthly Pattern"
              onChange={(e) => {
                if (e.target.value === 'day') {
                  setMonthPosition(null)
                } else {
                  setMonthPosition(1)
                }
                handleFrequencyChange(frequency)
              }}
            >
              <MenuItem value="day">On day of month</MenuItem>
              <MenuItem value="position">On specific weekday</MenuItem>
            </Select>
          </FormControl>

          {monthPosition === null ? (
            <TextField
              label="Day of month"
              type="number"
              value={monthDay}
              onChange={(e) => {
                setMonthDay(parseInt(e.target.value) || 1)
                handleFrequencyChange(frequency)
              }}
              inputProps={{ min: 1, max: 31 }}
              helperText="Day 1-31"
              fullWidth
            />
          ) : (
            <Box>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Position</InputLabel>
                <Select
                  value={monthPosition}
                  label="Position"
                  onChange={(e) => {
                    setMonthPosition(e.target.value as number)
                    handleFrequencyChange(frequency)
                  }}
                >
                  <MenuItem value={1}>First</MenuItem>
                  <MenuItem value={2}>Second</MenuItem>
                  <MenuItem value={3}>Third</MenuItem>
                  <MenuItem value={4}>Fourth</MenuItem>
                  <MenuItem value={-1}>Last</MenuItem>
                </Select>
              </FormControl>

              <Typography variant="body2" color="text.secondary" gutterBottom>
                Day of week
              </Typography>
              <ToggleButtonGroup
                value={selectedDays}
                exclusive
                onChange={(_, newDay) => {
                  if (newDay !== null) {
                    setSelectedDays([newDay])
                    handleFrequencyChange(frequency)
                  }
                }}
                aria-label="day of week"
                sx={{ flexWrap: 'wrap' }}
              >
                {dayLabels.map((day, index) => (
                  <ToggleButton
                    key={day}
                    value={index}
                    aria-label={day}
                    sx={{ minWidth: 45 }}
                  >
                    {day}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Box>
          )}
        </Box>
      )}

      {/* Description */}
      {value && (
        <Chip
          label={getRRuleDescription(value)}
          color="primary"
          variant="outlined"
          sx={{ alignSelf: 'flex-start' }}
        />
      )}
    </Box>
  )
}
